import { AdminMenuItem, ItemAdminInfo, User } from 'frontend/types';
import VueRouter from 'vue-router';
import retool from 'frontend/utils/retool';

export interface AdminInfoObject {
  published: boolean;
  coming_soon?: boolean;
  admin_info?: ItemAdminInfo;
  lessons_enabled?: boolean;
  version_name?: string;
  slug?: string;
}

export type AdminMenuNoun =
  | 'course'
  | 'chapter'
  | 'versioned_chapter'
  | 'lesson'
  | 'quiz'
  | 'learning path'
  | 'group';

interface MenuItems {
  items: AdminMenuItem[];
  badges: string[];
}

export function populateAdminMenu(
  obj: AdminInfoObject,
  objNoun: AdminMenuNoun,
  router?: VueRouter,
  useRetool?: boolean,
): MenuItems | null {
  if (!obj.admin_info) {
    return null;
  }

  const items: AdminMenuItem[] = [];
  const badges: string[] = [];
  if (!obj.published && objNoun !== 'lesson') {
    items.push('Unpublished');
    badges.push('U');
  }

  if (obj.coming_soon) {
    items.push('Coming soon');
    badges.push('CS');
  }

  if (obj.admin_info.alternative_link_info) {
    items.push({
      link: [
        obj.admin_info.alternative_link_info.link as string,
        obj.admin_info.alternative_link_info.link_text as string,
      ],
    });
  }

  switch (objNoun) {
    case 'course': {
      items.push({
        eventName: 'reset-course-state',
        linkText: 'Reset all saved state',
      });
      if (obj.admin_info.responsible_staff_info) {
        items.push({
          htmlLink: [obj.admin_info.responsible_staff_info as string, 'Responsible staff'],
        });
      }

      if (obj.version_name && obj.version_name !== '' && obj.slug && router) {
        // This is a versioned course model
        badges.push(`${obj.version_name} — ${obj.admin_info.state?.toUpperCase()}`);

        items.push({
          link: [
            useRetool
              ? retool.courseVersions(obj.slug)
              : router.resolve({ name: 'course_versions', params: { courseSlug: obj.slug } }).href,
            'View all course versions',
          ],
        });

        const [verbUpper, verbLower] =
          obj.admin_info.state === 'Draft' ? ['Edit', 'edit'] : ['View', 'view'];

        items.push({
          link: [
            router.resolve({
              name: 'course_edit',
              params: { courseSlug: obj.slug, versionName: obj.version_name },
            }).href,
            `${verbUpper} course version ${obj.version_name} in Campgrounds`,
          ],
        });

        if (obj.admin_info.edit_url) {
          items.push({
            link: [
              obj.admin_info.edit_url,
              `Admin ${verbLower} course version ${obj.version_name}`,
            ],
          });
        }
      } else {
        // This is an unversioned course model
        if (obj.admin_info.edit_url) {
          items.push({ link: [obj.admin_info.edit_url, 'Edit unversioned course'] });
        }
      }

      break;
    }

    case 'chapter': {
      if (obj.admin_info.responsible_staff_info) {
        items.push({
          htmlLink: [obj.admin_info.responsible_staff_info as string, 'Responsible staff'],
        });
      }

      if (obj.admin_info.edit_url) {
        items.push({ link: [obj.admin_info.edit_url, `Edit chapter`] });
      }

      if (obj.lessons_enabled === true) {
        badges.push('v3');
      } else if (obj.admin_info.create_quiz_url) {
        items.push({
          link: [obj.admin_info.create_quiz_url as string, 'Create quiz'],
        });
      }

      break;
    }

    case 'lesson': {
      // No badge for lessons for now.
      // badges.push('v3');

      if (obj.admin_info.responsible_staff_info) {
        items.push({
          htmlLink: [obj.admin_info.responsible_staff_info as string, 'Responsible staff'],
        });
      }

      if (obj.admin_info.edit_url) {
        items.push({
          link: [
            useRetool ? retool.lessonVersions(obj.slug!) : obj.admin_info.edit_url,
            'View all versions',
          ],
        });
      }
      break;
    }

    case 'versioned_chapter': {
      break;
    }

    default: {
      if (obj.admin_info.edit_url) {
        items.push({ link: [obj.admin_info.edit_url, `Edit ${objNoun}`] });
      }
      break;
    }
  }

  if (obj.admin_info.direct_link) {
    items.push({
      link: [obj.admin_info.direct_link, 'Direct link'],
    });
  }

  return { items, badges };
}

export function withUserActions(user: User, menuItems: MenuItems | null): MenuItems | null {
  if (menuItems) {
    const { items } = menuItems;
    if (user.is_classroom_admin) {
      items.push({
        eventName: 'bulk-reset-course-state',
        linkText: 'BfE: UNENROLL all classrooms users',
      });
    }
  }

  return menuItems;
}
